import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useClientQuery } from 'hooks/apollo';
import {
  LEASING_SERVICE_AMOUNT_TYPES,
  LEASING_SERVICE_CALC_TYPES,
  LEASING_SERVICE_CATEGORIES,
  LEASING_SERVICE_CLASSES
} from 'features/leasingServiceType/queries';
import { Autocomplete, Card, TextField } from '@totalsoft/rocket-ui';
import { FormControlLabel, Grid } from '@mui/material';
import TopicIcon from '@mui/icons-material/Topic';
import { get, over, set } from '@totalsoft/change-tracking-react';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { getAsyncAutocompleteNoOptionsText, valueOrDefault } from 'utils/functions';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { useTranslation } from 'react-i18next';
import { ThinCheckbox } from 'components/common/Styles';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { LINK_ITEMS, SUPPLIERS } from 'features/common/queries';

const LeasingServiceTypeGeneralData = ({ lens, validation, disabled }) => {
  const { t } = useTranslation();
  const clientQuery = useClientQuery();
  const { cvsChPercent, cvsChAmount } = useFieldDecimalPlacesConfig();

  const { data: classesData } = useQuery(LEASING_SERVICE_CLASSES);

  const { data: amountTypesData } = useQuery(LEASING_SERVICE_AMOUNT_TYPES);

  const { data: calcTypesData } = useQuery(LEASING_SERVICE_CALC_TYPES);

  const { data: categoriesData } = useQuery(LEASING_SERVICE_CATEGORIES);

  const classes = useMemo(() => classesData?.leasingServiceClasses || [], [classesData]);
  const amountTypes = useMemo(() => amountTypesData?.leasingServiceAmountTypes || [], [amountTypesData]);
  const calcTypes = useMemo(() => calcTypesData?.leasingServiceCalcTypes || [], [calcTypesData]);
  const categories = useMemo(() => categoriesData?.leasingServiceCategories || [], [categoriesData]);

  const serviceKinds = useMemo(
    () => [
      { id: 0, name: t('LeasingServiceType.KindExternal') },
      { id: 1, name: t('LeasingServiceType.KindFinanced') },
      { id: 2, name: t('LeasingServiceType.KindResold') }
    ],
    [t]
  );

  // #region Link item

  const linkItem = { id: lens.linkItemId |> get, name: lens.linkItemName |> get };

  const loadLinkItems = useCallback(
    async search => {
      const { data } = await clientQuery(LINK_ITEMS, {
        variables: { search }
      });
      return data?.linkItems || [];
    },
    [clientQuery]
  );

  const handleLinkItemChange = ({ id, name }) => {
    over(lens, prev => ({ ...prev, linkItemId: id, linkItemName: name }));
  };

  // #endregion

  // #region Supplier

  const supplier = { id: lens.supplierId |> get, name: lens.supplierName |> get };

  const loadSuppliers = useCallback(
    async search => {
      const { data } = await clientQuery(SUPPLIERS, {
        variables: { search }
      });
      return data?.suppliers || [];
    },
    [clientQuery]
  );

  const handleSupplierChange = ({ id, name }) => {
    over(lens, prev => ({ ...prev, supplierId: id, supplierName: name }));
  };

  // #endregion

  return (
    <Card title={t('General.GeneralData')} icon={TopicIcon} iconColor='primary'>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('LeasingServiceType.Code')}
            fullWidth
            required
            value={lens.code |> get |> valueOrDefault('')}
            onChange={lens.code |> set}
            error={!isValid(validation.code)}
            helperText={getErrors(validation.code)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('LeasingServiceType.Name')}
            fullWidth
            required
            value={lens.name |> get |> valueOrDefault('')}
            onChange={lens.name |> set}
            error={!isValid(validation.name)}
            helperText={getErrors(validation.name)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            label={t('LeasingServiceType.Category')}
            value={lens.categoryId |> get}
            onChange={lens.categoryId |> set}
            simpleValue
            options={categories}
            error={!isValid(validation.categoryId)}
            helperText={getErrors(validation.categoryId)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            label={t('LeasingServiceType.CalculationClass')}
            value={lens.calculationClassId |> get}
            onChange={lens.calculationClassId |> set}
            simpleValue
            options={classes}
            error={!isValid(validation.calculationClassId)}
            helperText={getErrors(validation.calculationClassId)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            label={t('LeasingServiceType.Kind')}
            value={lens.kindId |> get}
            onChange={lens.kindId |> set}
            required
            simpleValue
            options={serviceKinds}
            error={!isValid(validation.kindId)}
            helperText={getErrors(validation.kindId)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            label={t('LeasingServiceType.CalcType')}
            value={lens.calcTypeId |> get}
            onChange={lens.calcTypeId |> set}
            simpleValue
            options={calcTypes}
            error={!isValid(validation.calcTypeId)}
            helperText={getErrors(validation.calcTypeId)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            label={t('LeasingServiceType.AmountType')}
            value={lens.amountTypeId |> get}
            onChange={lens.amountTypeId |> set}
            simpleValue
            options={amountTypes}
            error={!isValid(validation.amountTypeId)}
            helperText={getErrors(validation.amountTypeId)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('LeasingServiceType.DefaultPercent')}
            fullWidth
            isNumeric
            decimalScale={cvsChPercent}
            value={lens.defaultPercent |> get |> valueOrDefault('')}
            onChange={lens.defaultPercent |> set}
            error={!isValid(validation.defaultPercent)}
            helperText={getErrors(validation.defaultPercent)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('LeasingServiceType.DefaultValue')}
            fullWidth
            isNumeric
            decimalScale={cvsChAmount}
            value={lens.defaultValue |> get |> valueOrDefault('')}
            onChange={lens.defaultValue |> set}
            error={!isValid(validation.defaultValue)}
            helperText={getErrors(validation.defaultValue)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            label={t('LeasingServiceType.LinkItem')}
            value={linkItem}
            onChange={handleLinkItemChange}
            loadOptions={loadLinkItems}
            error={!isValid(validation.linkItemId)}
            helperText={getErrors(validation.linkItemId)}
            disabled={disabled}
            noOptionsText={getAsyncAutocompleteNoOptionsText}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('LeasingServiceType.Periodicity')}
            fullWidth
            isNumeric
            required
            decimalScale={0}
            value={lens.periodicity |> get |> valueOrDefault('')}
            onChange={lens.periodicity |> set}
            error={!isValid(validation.periodicity)}
            helperText={getErrors(validation.periodicity)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            label={t('LeasingServiceType.Supplier')}
            value={supplier}
            onChange={handleSupplierChange}
            loadOptions={loadSuppliers}
            error={!isValid(validation.supplierId)}
            helperText={getErrors(validation.supplierId)}
            disabled={disabled}
            noOptionsText={getAsyncAutocompleteNoOptionsText}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('LeasingServiceType.STVersion')}
            fullWidth
            isNumeric
            decimalScale={0}
            value={lens.stVersion |> get |> valueOrDefault('')}
            onChange={lens.stVersion |> set}
            error={!isValid(validation.stVersion)}
            helperText={getErrors(validation.stVersion)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4} alignSelf='end'>
          <FormControlLabel
            label={t('LeasingServiceType.PercentOrValue')}
            control={
              <ThinCheckbox
                checked={lens.percentOrValue |> get |> valueOrDefault(false)}
                onChange={lens.percentOrValue |> set |> onCheckBoxChange}
                disabled={disabled}
              />
            }
            disabled={disabled}
            sx={{ ml: 0 }}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

LeasingServiceTypeGeneralData.propTypes = {
  lens: PropTypes.object,
  validation: PropTypes.object,
  disabled: PropTypes.bool
};

export default LeasingServiceTypeGeneralData;
